import { useEffect, useState } from "react";
import { useParams } from "react-router";
import useWebSocket from "react-use-websocket";
import { Check, CheckCircle2, Loader2, Printer, Radio } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

function AjusteRoloAutomaticoSocket() {
  const { id_modulo } = useParams();
  const [socketUrl, setSocketUrl] = useState(import.meta.env.VITE_WS_URL);
  const [calibrou, setCalibrou] = useState(false);
  const [prioridade, setPrioridade] = useState("");
  const [padraoVirgem, setPadraoVirgem] = useState("");

  useWebSocket(socketUrl, {
    heartbeat: {
      message: "ping",
      interval: 5 * 60 * 1000,
    },
    shouldReconnect: () => true,
    reconnectAttempts: 10,
    reconnectInterval: (attemptNumber) =>
      Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
    onMessage(event) {
      const dataString: string = event.data;

      // Só o resultado da calibração interessa aqui:
      //   rtskpdptv|<maior|menor>|<inicio da tag virgem>|<id_config>
      //
      // Sem esse filtro qualquer mensagem do módulo caía no split("|") abaixo.
      // A pior é a que ele manda sozinho ao conectar,
      // "Modulo: INFO|<rede>|<senha>|WFCN|...": os campos 1 e 2 são o SSID e a
      // SENHA do WiFi, que apareciam na tela como prioridade e padrão de tag
      // virgem — e ainda davam a calibração como concluída antes de ela rodar.
      if (!dataString.startsWith("rtskpdptv|")) return;

      const partes = dataString.split("|");
      const prioridadeRaw = partes[1]?.trim().toLowerCase() || "";
      const tagVirgemInicio = partes[2]?.trim() || "";

      if (prioridadeRaw.includes("maior")) {
        setPrioridade("Priorizar maior serial");
      } else if (prioridadeRaw.includes("menor")) {
        setPrioridade("Priorizar menor serial");
      } else {
        setPrioridade(prioridadeRaw || "—");
      }
      setPadraoVirgem(tagVirgemInicio || "—");
      setCalibrou(true);
    },
    share: true,
  });

  useEffect(() => {
    if (id_modulo) {
      setSocketUrl(`${import.meta.env.VITE_WS_URL}/${id_modulo}`);
    }
  }, [id_modulo]);

  return (
    <div className="relative min-h-svh flex items-center justify-center p-4 bg-gradient-to-br from-primary/20 via-primary/5 to-background overflow-hidden">
      {/* Decoração de fundo */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute -top-32 -left-32 w-72 h-72 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-72 h-72 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <Card className="relative w-full max-w-sm border-border/60 shadow-xl backdrop-blur-sm">
        <CardContent className="p-6 flex flex-col items-center text-center gap-5">
          {/* Ícone de status */}
          <div className="relative">
            {!calibrou && (
              <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
            )}
            <div
              className={`relative flex h-20 w-20 items-center justify-center rounded-full transition-colors duration-500 ${
                calibrou
                  ? "bg-emerald-500/10 ring-4 ring-emerald-500/15"
                  : "bg-primary/10 ring-4 ring-primary/10"
              }`}
            >
              {calibrou ? (
                <CheckCircle2 className="h-10 w-10 text-emerald-500 animate-in zoom-in-50 duration-500" />
              ) : (
                <Loader2 className="h-9 w-9 text-primary animate-spin" />
              )}
            </div>
          </div>

          {/* Título + descrição */}
          <div className="space-y-1.5">
            <h1 className="text-xl font-semibold tracking-tight">
              {calibrou ? "Calibração concluída" : "Calibrando impressora"}
            </h1>
            <p className="text-sm text-muted-foreground text-balance">
              {calibrou
                ? "A impressora foi calibrada com sucesso e está pronta para uso."
                : "Aguarde enquanto detectamos o padrão do rolo de etiquetas."}
            </p>
          </div>

          {/* Resultado da calibração */}
          {calibrou && (
            <>
              <Separator />
              <div className="w-full space-y-3 text-left animate-in fade-in slide-in-from-bottom-2 duration-500">
                <DataRow
                  icon={<Radio className="h-4 w-4 text-primary" />}
                  label="Prioridade do rolo"
                  value={prioridade}
                />
                <DataRow
                  icon={<Printer className="h-4 w-4 text-primary" />}
                  label="Padrão do rolo"
                  value={padraoVirgem}
                />
              </div>

              <Button
                className="w-full animate-in fade-in slide-in-from-bottom-2 duration-500"
                onClick={() => {
                  window.close();
                  // Fallback: se a aba não foi aberta via script,
                  // window.close() é bloqueado pelo browser.
                  setTimeout(() => {
                    if (!window.closed) {
                      window.location.href = "about:blank";
                    }
                  }, 150);
                }}
              >
                <Check className="h-4 w-4 mr-2" />
                Concluir
              </Button>
            </>
          )}

          {/* Footer com identificação do módulo */}
          {id_modulo && (
            <p className="text-[10px] text-muted-foreground/70 tracking-wider uppercase">
              Módulo {id_modulo}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DataRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <Badge variant="secondary" className="font-medium">
        {value || "—"}
      </Badge>
    </div>
  );
}

export default AjusteRoloAutomaticoSocket;
